import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';
import { siteConfig } from './siteData';
import { useArticles } from './hooks/articles';
import { useHeroSectionData, useCollaboratingCompanies, useImportantDates, useTeachers, useStatistics, useWebCourses, useSubmitContactForm } from './hooks/sectionHooks';
import TeacherModal from './components/TeacherModal';
import Footer from './components/Footer';
import LoadingSpinner, { LoadingBoundary } from './components/LoadingSpinner';
import HeroSkeleton from './components/HeroSkeleton';

function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [showCoursesDropdown, setShowCoursesDropdown] = useState(false);
  const [hideTimeout, setHideTimeout] = useState(null);
  const [hideCoursesTimeout, setHideCoursesTimeout] = useState(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const navigate = useNavigate();
  
  // Contact form mutation
  const { mutate: submitContact, isPending: contactSubmitting, isSuccess: contactSuccess, error: contactError, reset: resetContactMutation } = useSubmitContactForm();
  const { data: articles, isLoading: loading, error } = useArticles();
  const { data: importantDatesData, isLoading: datesLoading } = useImportantDates();
  const { data: teachersData, isLoading: teachersLoading } = useTeachers();
  const { data: collaboratingCompaniesData, isLoading: companiesLoading } = useCollaboratingCompanies();
  const { data: statisticsData, isLoading: statisticsLoading } = useStatistics();
  const { data: webCoursesData, isLoading: coursesLoading } = useWebCourses();
  const { data: heroData, isLoading: heroLoading } = useHeroSectionData();
  
  // Get unique course types from data
  const courseTypes = Array.isArray(webCoursesData) 
    ? [...new Set(webCoursesData.map(course => course.course_type))].sort((firstType, secondType) => {
        if (firstType === 'CFA') return -1;
        if (secondType === 'CFA') return 1;
        return String(firstType).localeCompare(String(secondType));
      })
    : [];
  
  console.log('Fetched articles:', articles);

  const handleShowDropdown = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    setShowAboutDropdown(true);
  };

  const handleHideDropdown = () => {
    const timeout = setTimeout(() => {
      setShowAboutDropdown(false);
    }, 200);
    setHideTimeout(timeout);
  };

  const handleShowCoursesDropdown = () => {
    if (hideCoursesTimeout) {
      clearTimeout(hideCoursesTimeout);
      setHideCoursesTimeout(null);
    }
    setShowCoursesDropdown(true);
  };

  const handleHideCoursesDropdown = () => {
    const timeout = setTimeout(() => {
      setShowCoursesDropdown(false);
    }, 200);
    setHideCoursesTimeout(timeout);
  };

  // Contact form handlers
  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    resetContactMutation();
    
    submitContact(contactForm, {
      onSuccess: () => {
        setContactForm({ name: '', email: '', subject: '', message: '' });
      }
    });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Hero slider data
  const heroSlides = [
    {
      title: "ИРЭЭДҮЙДЭЭ ХӨРӨНГӨ ОРУУЛ",
      subtitle: "Дэлхийн стандартад нийцсэн мэдлэг чадвар, ёс зүйтэй мэргэжилтнүүдийг бэлтгэх",
      backgroundImage: "https://i0.wp.com/edu-icer.mn/wp-content/uploads/2025/05/76826-cover.jpg?resize=1080%2C362&ssl=1"
    },
    {
      title: "CFA СУРГАЛТ",
      subtitle: "Олон улсын стандартад нийцсэн санхүүгийн шинжээчийн мэргэжил",
      backgroundImage: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1600&h=600&fit=crop"
    },
    {
      title: "FYP СУРГАЛТ",
      subtitle: "Санхүүгийн төлөвлөлтийн мэргэжлийн өндөр түвшний сургалт",
      backgroundImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1600&h=600&fit=crop"
    }
  ];

  // Use hero data from API or fallback
  const displayHero = Array.isArray(heroData) && heroData.length > 0 ? heroData : heroSlides;
  console.log('Displaying hero slides:', displayHero);

  // Auto-play slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % displayHero.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [displayHero.length]);

  // Use articles from API or fallback to static data
  const displayArticles = Array.isArray(articles) && articles.length > 0 ? articles : siteConfig.featuredStories;
  const displayDates = Array.isArray(importantDatesData) && importantDatesData.length > 0 ? importantDatesData : (siteConfig.importantDates || []);
  const displayTeachers = Array.isArray(teachersData) && teachersData.length > 0 ? teachersData : siteConfig.teachers;
  const displayCompanies = Array.isArray(collaboratingCompaniesData) && collaboratingCompaniesData.length > 0 ? collaboratingCompaniesData : siteConfig.collaboratingCompanies;
  const displayStatistics = Array.isArray(statisticsData) && statisticsData.length > 0 ? statisticsData : siteConfig.statistics;
  
  console.log('Displaying articles:', displayArticles);

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src={siteConfig.logo.src} alt={siteConfig.logo.alt} />
          </div>
          <button 
            className="mobile-menu-button" 
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className={isMobileMenuOpen ? "hamburger open" : "hamburger"}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <nav className={isMobileMenuOpen ? "nav-open" : ""}>
            <ul className="nav-menu">
              {siteConfig.navigation.map((item, index) => (
                <li 
                  key={index}
                  className={(item.label === "Бидний тухай" || item.label === "Сургалт") ? "nav-item-with-dropdown" : ""}
                  onMouseEnter={() => {
                    if (item.label === "Бидний тухай") handleShowDropdown();
                    if (item.label === "Сургалт") handleShowCoursesDropdown();
                  }}
                  onMouseLeave={() => {
                    if (item.label === "Бидний тухай") handleHideDropdown();
                    if (item.label === "Сургалт") handleHideCoursesDropdown();
                  }}
                >
                  <a 
                    href={item.link} 
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={item.label === "Суралцагчийн булан" ? "nav-button" : ""}
                    target={item.label === "Суралцагчийн булан" ? "_blank" : undefined}
                    rel={item.label === "Суралцагчийн булан" ? "noopener noreferrer" : undefined}
                  >
                    {item.label}
                  </a>
                  {item.label === "Бидний тухай" && showAboutDropdown && (
                    <div 
                      className="nav-dropdown"
                      onMouseEnter={handleShowDropdown}
                      onMouseLeave={handleHideDropdown}
                    >
                      <button 
                        className="nav-dropdown-item" 
                        onClick={() => {
                          navigate('/teachers');
                          setShowAboutDropdown(false);
                        }}
                      >
                        Багш нар
                      </button>
                    </div>
                  )}
                  {item.label === "Сургалт" && showCoursesDropdown && (coursesLoading || courseTypes.length > 0) && (
                    <div 
                      className="nav-dropdown"
                      onMouseEnter={handleShowCoursesDropdown}
                      onMouseLeave={handleHideCoursesDropdown}
                    >
                      {coursesLoading && <LoadingSpinner className="loading-indicator-compact" />}
                      {courseTypes.map((type) => (
                        <button 
                          key={type}
                          className="nav-dropdown-item" 
                          onClick={() => {
                            navigate(`/courses?type=${type}`);
                            setShowCoursesDropdown(false);
                          }}
                        >
                          {type} сургалт
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </header>

      {/* Quick Links Bar */}
      {/* <div className="quick-links">
        <div className="quick-links-container">
          {siteConfig.quickLinks.map((link, index) => (
            <a key={index} href={link.link}>
              {link.label}
            </a>
          ))}
        </div>
      </div> */}

      {/* Hero Section - Video Slider */}
      <section className="hero-slider">
        <div className="slider-container">
          <LoadingBoundary isLoading={heroLoading} fallback={<HeroSkeleton />}>
            {displayHero.map((slide, index) => {
              const videoUrl = slide.background_image || '';
              const isWebm = videoUrl.toLowerCase().endsWith('.webm');
              const isMp4 = videoUrl.toLowerCase().endsWith('.mp4');
              const isVideo = isWebm || isMp4;

              return (
                <div
                  key={index}
                  className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
                >
                  {isVideo ? (
                    <video
                      className="hero-video-bg"
                      autoPlay
                      muted
                      loop
                      playsInline
                      key={videoUrl}
                    >
                      <source src={videoUrl} type={isWebm ? 'video/webm' : 'video/mp4'} />
                    </video>
                  ) : (
                    <div
                      className="hero-image-bg"
                      style={{ backgroundImage: `url(${videoUrl})` }}
                    />
                  )}
                  <div className="hero-overlay"></div>
                  <div className="hero-content">
                    <h1 className="hero-title">
                      {slide.title?.split(' ').map((word, wordIndex) => (
                        <span
                        className="hero-word"
                          key={wordIndex}
                          style={{ animationDelay: `${wordIndex * 0.15}s` }}
                        >
                          {word}
                        </span>
                      ))}
                    </h1>
                    <p className="hero-subtitle">
                      {slide.subtitle?.split(' ').map((word, wordIndex) => (
                        <span
                          key={wordIndex}
                          className="hero-word subtitle-word"
                          style={{ animationDelay: `${0.5 + wordIndex * 0.1}s` }}
                        >
                          {word}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              );
            })}
          </LoadingBoundary>
          
          {/* Navigation Arrows */}
          {/* <button className="slider-arrow slider-arrow-left" onClick={prevSlide}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button className="slider-arrow slider-arrow-right" onClick={nextSlide}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button> */}
          
          {/* Dots Navigation */}
          {/* <div className="slider-dots">
            {displayHero.map((_, index) => (
              <button
                key={index} 
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              ></button>
            ))}
          </div> */}
        </div>
      </section>

      {/* Vision and Mission */}
      <section className="feature-cards-section vision-mission-section" aria-label="Алсын хараа, эрхэм зорилго">
        <div className="feature-cards-container">
          {siteConfig.featureCards.filter((card) => card.id === 1 || card.id === 2).map((card) => (
            <article key={card.id} className="feature-card">
              <div className="feature-icon">
                <img src={card.icon} alt="" />
              </div>
              <h2 className="feature-title">{card.title}</h2>
              <p className="feature-description">{card.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Statistics Section */}
      <section className="statistics">
        <div className="statistics-container">
          <LoadingBoundary isLoading={statisticsLoading}>
            {displayStatistics?.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </LoadingBoundary>
        </div>
      </section>

      

      {/* Main Content */}
      <main className="main-content">
        {/* New Layout: Top Stories Left + Chronicle Right */}
        <section className="content-layout">
          <div className="content-container">
            {/* Top Featured Story - Left Side */}
            <div className="featured-stories-left">
              {loading && <LoadingSpinner />}
              {error && <p style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>Error loading articles. Showing default content.</p>}
              {!loading && displayArticles.length > 0 && (
                <div 
                  className="featured-main-card"
                  onClick={() => navigate(`/article/${displayArticles[0].id}`)}
                  style={{ 
                    cursor: 'pointer',
                    backgroundImage: `url(${displayArticles[0].asset})`
                  }}
                >
                  <div className="featured-main-overlay">
                    <span className="featured-category">{displayArticles[0].category || 'NEWS'}</span>
                    <h2 className="featured-main-title">{displayArticles[0].title}</h2>
                    <div className="featured-meta">
                      <span className="featured-author">Admin</span>
                      <span className="featured-read-time">○ 3 min read</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chronicle - Right Side */}
            <div className="chronicle-sidebar">
              <h2>Articles</h2>
              {loading && <LoadingSpinner />}
              {!loading && <div className="chronicle-list">
                {displayArticles.slice(1, 5).map((story) => (
                  <div 
                    key={story.id} 
                    className="chronicle-item"
                    onClick={() => navigate(`/article/${story.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="chronicle-thumb">
                      <img src={story.asset} alt={story.title} />
                    </div>
                    <div className="chronicle-content">
                      <span className="chronicle-category">{story.category || 'NEWS'}</span>
                      <h3 className="chronicle-title">{story.title}</h3>
                      <div className="chronicle-meta">
                        <span className="chronicle-author">Admin</span>
                        {/* <span className="chronicle-read-time">○ 3 min read</span> */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>}
              {/* <div className="chronicle-footer">
                <button 
                  onClick={() => {
                    navigate('/articles');
                    window.scrollTo(0, 0);
                  }}
                  className="chronicle-link"
                >
                  More news
                </button>
                <span className="chronicle-separator">|</span>
                <button 
                  onClick={() => {
                    navigate('/articles');
                    window.scrollTo(0, 0);
                  }}
                  className="chronicle-link"
                >
                  For media
                </button>
              </div> */}
            </div>
          </div>
        </section>
      </main>

      {/* Important Dates Marquee */}
      <section className="dates-marquee-section">
        <LoadingBoundary isLoading={datesLoading} className="loading-indicator-compact">
          <div className="dates-marquee-track">
            <div className="dates-marquee-content">
              {displayDates?.map((date, index) => (
                <span key={index} className="dates-marquee-item">
                  <span className="dates-marquee-date">{date.date}</span>
                  <span className="dates-marquee-title">{date.title}</span>
                </span>
              ))}
              {displayDates?.map((date, index) => (
                <span key={`dup-${index}`} className="dates-marquee-item">
                  <span className="dates-marquee-date">{date.date} - </span>
                  <span className="dates-marquee-title">{date.event}</span>
                </span>
              ))}
            </div>
          </div>
        </LoadingBoundary>
      </section>

      {/* Courses grouped by the type returned by the API */}
      <LoadingBoundary isLoading={coursesLoading}>
        {courseTypes.map((courseType, sectionIndex) => {
          const courses = webCoursesData
            .filter(course => course.course_type === courseType)
            .sort((firstCourse, secondCourse) => Number(firstCourse.id) - Number(secondCourse.id));

          return (
            <section
              key={courseType}
              className={`courses-grid-section ${sectionIndex % 2 === 1 ? 'courses-grid-section-alt' : ''}`}
            >
              <div className="courses-grid-container">
                <div className="courses-grid-header">
                  <h2 className="courses-grid-title">
                    <span className="title-highlight">{courseType}</span><br/>
                    <span className="title-highlight">сургалт</span>
                  </h2>
                </div>
                <div className="courses-grid-cards">
                  {courses.map((course, index) => (
                    <div
                      key={course.id}
                      className={`course-grid-card ${index === 0 ? 'expanded' : ''}`}
                      onClick={() => navigate(`/course/${course.id}`)}
                    >
                      <div className="course-grid-card-bg"></div>
                      <div className="course-grid-card-label">
                        <span>{course.course_type}</span>
                      </div>
                      <div className="course-grid-card-content">
                        <h3>{course.name}</h3>
                        <p className="course-grid-card-desc" dangerouslySetInnerHTML={{ __html: course.description }}></p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </LoadingBoundary>
      

      {/* Teachers Section */}
      <section className="team-section">
        <div className="team-container">
          <div className="team-header">
            <h2 className="team-title">Багш нар</h2>
            {/* <p className="team-subtitle">Meet the talented individuals who drive our company's success with their dedication, expertise, and passion for innovation</p> */}
          </div>
          <div className="team-grid">
            <LoadingBoundary isLoading={teachersLoading}>
              {displayTeachers?.map((teacher) => (
                <div key={teacher.id} className="team-card" onClick={() => setSelectedTeacher(teacher)}>
                  <div className="team-card-image">
                    <img
                      src={teacher.image}
                      alt={teacher.name}
                    />
                  </div>
                  <div className="team-card-info">
                    <div className="team-card-details">
                      <h3 className="team-card-name">{teacher.name}</h3>
                      <p className="team-card-title">{teacher.title}</p>
                    </div>
                    {/* <a href={teacher.linkedin || '#'} className="team-card-linkedin" onClick={(e) => e.stopPropagation()}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a> */}
                  </div>
                </div>
              ))}
            </LoadingBoundary>
          </div>
        </div>
      </section>

      {/* Teacher Modal */}
      {selectedTeacher && (
        <TeacherModal 
          teacher={selectedTeacher} 
          onClose={() => setSelectedTeacher(null)} 
        />
      )}

      {/* Collaborating Companies Section */}
      <section className="companies-section">
        <div className="companies-container">
          <h2 className="companies-title">ХАМТРАГЧ БАЙГУУЛЛАГУУД</h2>
          <div className="companies-grid">
            <LoadingBoundary isLoading={companiesLoading}>
              {displayCompanies?.map((company) => (
                <div key={company.id} className="company-item">
                  <a href={company.website} target="_blank" rel="noopener noreferrer">
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="company-logo"
                    />
                  </a>
                </div>
              ))}
            </LoadingBoundary>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="contact-section">
        <div className="contact-header">
          <h2 className="contact-main-title">
            <span className="contact-title-underline">Бидэнтэй</span> холбогдох
          </h2>
          <p className="contact-subtitle">
            Бид таны санал хүсэлтийг хүлээн авахад үргэлж бэлэн байдаг. Доорх мэдээллээр бидэнтэй холбогдоно уу.
          </p>
        </div>
        <div className="contact-container">
          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <h3 className="contact-form-title">Саналт хүсэлт илгээх</h3>
            {contactSuccess && (
              <div className="contact-success-message">
                Таны месеж амжилттай илгээгдлээ. Бид тантай удахгүй холбогдох болно.
              </div>
            )}
            {contactError && (
              <div className="contact-error-message">
                {contactError.message || 'Мессеж илгээхэд алдаа гарлаа. Дахин оролдоно уу.'}
              </div>
            )}
            <form className="contact-form" onSubmit={handleContactSubmit}>
              <div className="contact-form-row">
                <input 
                  type="text" 
                  name="name"
                  value={contactForm.name}
                  onChange={handleContactInputChange}
                  placeholder="Нэрээ оруулна уу" 
                  className="contact-input"
                  required
                />
                <input 
                  type="email" 
                  name="email"
                  value={contactForm.email}
                  onChange={handleContactInputChange}
                  placeholder="И-мэйл хаягаа оруулна уу" 
                  className="contact-input"
                  required
                />
              </div>
              <input 
                type="text" 
                name="subject"
                value={contactForm.subject}
                onChange={handleContactInputChange}
                placeholder="Гарчиг" 
                className="contact-input contact-input-full"
                required
              />
              <textarea 
                name="message"
                value={contactForm.message}
                onChange={handleContactInputChange}
                placeholder="Санал хүсэлтээ бичнэ үү" 
                className="contact-textarea"
                rows="5"
                required
              ></textarea>
              <button 
                type="submit" 
                className="contact-submit-btn"
                disabled={contactSubmitting}
              >
                {contactSubmitting ? <LoadingSpinner inline label="Илгээж байна..." /> : 'Илгээх'}
              </button>
            </form>
          </div>

          {/* Contact Details */}
          <div className="contact-details-wrapper">
            <h3 className="contact-details-title">Холбоо барих</h3>
            <p className="contact-details-description">
              {/* Бид таны санал хүсэлтийг хүлээн авахад үргэлж бэлэн байдаг. Доорх мэдээллээр бидэнтэй холбогдоно уу. */}
            </p>
            <div className="contact-info-grid">
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div className="contact-info-content">
                  <h4>Хаяг</h4>
                  <p>{siteConfig.footer.address}</p>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div className="contact-info-content">
                  <h4>Утас</h4>
                  <p>{siteConfig.footer.phone}</p>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div className="contact-info-content">
                  <h4>Ажлын цаг</h4>
                  <p>09 am - 06 pm</p>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <div className="contact-info-content">
                  <h4>Email</h4>
                  <p>{siteConfig.footer.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default App;
